<template>
  <div class="tmap-picker">
    <div ref="mapEl" class="tmap-el" :class="{ drawing: drawing }" :style="heightStyle"></div>
    <div v-if="!tmapReady" class="tmap-tip">地图组件加载中或当前无可用地图密钥…</div>
    <div v-if="mode === 'zone'" class="tmap-bar">
      <el-button size="mini" :type="drawing ? 'warning' : 'primary'" @click="toggleDraw">
        {{ drawing ? '结束绘制' : '开始绘制' }}
      </el-button>
      <el-button size="mini" icon="el-icon-refresh-left" :disabled="!points.length" @click="undoPoint">撤销上一点</el-button>
      <el-button size="mini" icon="el-icon-delete" type="danger" plain :disabled="!points.length" @click="clearPoints">清除</el-button>
      <span class="tmap-hint">
        {{ drawing ? '点击地图添加边界点 · 点击已有顶点删除该点 · 双击结束绘制' : '点击「开始绘制」后在地图上圈选围栏' }}
      </span>
    </div>
    <div v-else-if="mode === 'point'" class="tmap-bar">
      <span class="tmap-hint">在地图上点击选择位置，或在右侧表单手动填写经纬度</span>
    </div>
    <div v-else-if="mode === 'track'" class="tmap-bar">
      <span class="tmap-hint">绿色起点 · 红色终点 · 蓝色脉冲为当前播放位置 · 蓝线已走 / 灰线未走 / 红线超速段</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TMapPicker',
  props: {
    mode: { type: String, default: 'point' }, // point | zone | markers | track
    center: { type: Object, default: () => ({ lat: 30.2741, lng: 120.1551 }) },
    value: { type: [Object, Array], default: () => (null) },
    markers: { type: Array, default: () => [] },
    height: { type: [Number, String], default: 360 }, // 支持数字 px 或 '100%' 字符串
    // track 模式专用
    trackPoints: { type: Array, default: () => [] }, // [{lat, lng, over?}]
    trackCurrent: { type: Number, default: 0 },       // 当前播放位置索引
    trackPos: { type: Object, default: null }         // 当前播放位置插值坐标 {lat, lng}（平滑动画）
  },
  data() {
    return {
      map: null,
      markerLayer: null,
      pointLayer: null,
      labelLayer: null,
      polygon: null,
      polyline: null,
      // track 模式图层
      trackAllLine: null,      // 完整轨迹 polyline（灰）
      trackPassedLine: null,   // 已走部分 polyline（蓝）
      trackOverLines: null,    // 超速段 polyline（红）
      trackStartMarker: null,
      trackEndMarker: null,
      trackCurrentMarker: null,
      points: [],
      drawing: false,
      tmapReady: false,
      pointClickBound: false,
      _ro: null
    }
  },
  computed: {
    // 支持数字(px)或百分比字符串，避免 '100%px' 非法高度
    heightStyle() {
      if (typeof this.height === 'string') return { height: this.height }
      return { height: this.height + 'px' }
    }
  },
  watch: {
    center: {
      deep: true,
      handler(val) {
        if (this.map && val) {
          this.map.setCenter(new window.TMap.LatLng(val.lat, val.lng))
        }
      }
    },
    value: {
      deep: true,
      handler(val) {
        if (this.mode !== 'zone') return
        const next = Array.isArray(val) ? val.map(p => ({ lat: +p.lat, lng: +p.lng })) : []
        if (JSON.stringify(next) !== JSON.stringify(this.points)) {
          this.points = next
          this.drawing = false
          if (this.tmapReady) {
            this.drawPolygon()
            this.drawPolyline()
            this.drawPointMarkers()
          }
        }
      }
    },
    markers: {
      deep: true,
      handler() {
        if (this.mode === 'markers' && this.tmapReady) this.renderMarkers()
      }
    },
    trackPoints: {
      deep: true,
      handler(val) {
        if (this.mode === 'track' && this.tmapReady) this.renderTrack(val || [])
      }
    },
    trackCurrent() {
      if (this.mode === 'track' && this.tmapReady) this.updateTrackCurrent()
    },
    trackPos: {
      deep: true,
      handler() {
        // 插值位置更新：只移动当前位置 marker（平滑动画），不重建蓝线
        if (this.mode !== 'track' || !this.tmapReady) return
        const pos = this.trackPos
        const pts = this.trackPoints || []
        const fallback = pts[Math.min(Math.max(this.trackCurrent, 0), Math.max(pts.length - 1, 0))]
        const lat = pos && pos.lat != null ? pos.lat : (fallback ? fallback.lat : null)
        const lng = pos && pos.lng != null ? pos.lng : (fallback ? fallback.lng : null)
        if (lat == null || lng == null || !this.trackCurrentMarker) return
        try {
          this.trackCurrentMarker.setGeometries([{ styleId: 'cur', position: new window.TMap.LatLng(lat, lng) }])
        } catch (e) {}
      }
    }
  },
  mounted() {
    this.initMap()
    this.bindResize()
  },
  beforeDestroy() {
    if (this._ro) { this._ro.disconnect(); this._ro = null }
    if (this.map) {
      this.map.destroy && this.map.destroy()
      this.map = null
    }
  },
  methods: {
    initMap() {
      if (!window.TMap) {
        this.tmapReady = false
        return
      }
      try {
        const c = this.center || { lat: 30.2741, lng: 120.1551 }
        this.map = new window.TMap.Map(this.$refs.mapEl, {
          center: new window.TMap.LatLng(c.lat, c.lng),
          zoom: 14,
          viewMode: '2D'
        })
        this.tmapReady = true

        // 容器在抽屉动画/窗口变化期间尺寸会变，延迟触发一次 resize 修正点击偏移
        setTimeout(() => { if (this.map && this.map.resize) this.map.resize() }, 360)

        if (this.mode === 'markers') {
          this.renderMarkers()
        } else if (this.mode === 'zone') {
          this.points = Array.isArray(this.value) ? this.value.map(p => ({ lat: +p.lat, lng: +p.lng })) : []
          this.drawPolygon()
          this.drawPolyline()
          this.drawPointMarkers()
          this.map.on('click', this.onZoneClick)
          this.map.on('dblclick', this.onDblClick)
        } else if (this.mode === 'track') {
          this.renderTrack(this.trackPoints || [])
        } else {
          if (this.value && this.value.lat) {
            this.placePoint(this.value)
          }
          this.map.on('click', this.onPointClick)
        }
      } catch (e) {
        this.tmapReady = false
        console.error('TMap init failed', e)
      }
    },
    bindResize() {
      if (!window.ResizeObserver || !this.$refs.mapEl) return
      let timer = null
      this._ro = new window.ResizeObserver(() => {
        if (timer) clearTimeout(timer)
        timer = setTimeout(() => {
          if (this.map && this.map.resize) this.map.resize()
        }, 120)
      })
      this._ro.observe(this.$refs.mapEl)
    },
    resizeMap() {
      if (this.map && this.map.resize) this.map.resize()
    },
    /* ---------- track 模式：完整轨迹 + 已走/未走/超速段 + 当前位置 ---------- */
    clearTrack() {
      try { this.trackAllLine && this.trackAllLine.setMap(null) } catch (e) {}
      try { this.trackPassedLine && this.trackPassedLine.setMap(null) } catch (e) {}
      try { this.trackOverLines && this.trackOverLines.setMap(null) } catch (e) {}
      try { this.trackStartMarker && this.trackStartMarker.setMap(null) } catch (e) {}
      try { this.trackEndMarker && this.trackEndMarker.setMap(null) } catch (e) {}
      try { this.trackCurrentMarker && this.trackCurrentMarker.setMap(null) } catch (e) {}
      this.trackAllLine = this.trackPassedLine = this.trackOverLines = null
      this.trackStartMarker = this.trackEndMarker = this.trackCurrentMarker = null
    },
    renderTrack(pts) {
      if (!this.map || !window.TMap) return
      this.clearTrack()
      if (!pts.length) return
      try {
        const paths = pts.map(p => new window.TMap.LatLng(p.lat, p.lng))
        // 完整轨迹（灰色，作为未走背景）
        this.trackAllLine = new window.TMap.MultiPolyline({
          map: this.map,
          styles: { all: new window.TMap.PolylineStyle({ color: '#C9CDD4', width: 4, lineCap: 'round' }) },
          geometries: [{ styleId: 'all', paths }]
        })
        // 已走部分（亮蓝）
        const cur = Math.min(Math.max(this.trackCurrent, 0), pts.length - 1)
        if (cur > 0) {
          this.trackPassedLine = new window.TMap.MultiPolyline({
            map: this.map,
            styles: { passed: new window.TMap.PolylineStyle({ color: '#165DFF', width: 5, lineCap: 'round' }) },
            geometries: [{ styleId: 'passed', paths: paths.slice(0, cur + 1) }]
          })
        }
        // 超速段（红色）—— 连续 over=true 合并成段
        const overSegs = this._splitOverSegments(pts)
        if (overSegs.length) {
          this.trackOverLines = new window.TMap.MultiPolyline({
            map: this.map,
            styles: { over: new window.TMap.PolylineStyle({ color: '#F53F3F', width: 6, lineCap: 'round' }) },
            geometries: overSegs.map(seg => ({ styleId: 'over', paths: seg.map(p => new window.TMap.LatLng(p.lat, p.lng)) }))
          })
        }
        // 起点（绿）
        this.trackStartMarker = new window.TMap.MultiMarker({
          map: this.map,
          styles: {
            start: new window.TMap.MarkerStyle({
              width: 22, height: 28, src: this._svgMarker('#00B42A', '起'), anchor: { x: 11, y: 28 }
            })
          },
          geometries: [{ styleId: 'start', position: paths[0] }]
        })
        // 终点（红）
        this.trackEndMarker = new window.TMap.MultiMarker({
          map: this.map,
          styles: {
            end: new window.TMap.MarkerStyle({
              width: 22, height: 28, src: this._svgMarker('#F53F3F', '终'), anchor: { x: 11, y: 28 }
            })
          },
          geometries: [{ styleId: 'end', position: paths[paths.length - 1] }]
        })
        // 当前位置（蓝色脉冲）
        this.trackCurrentMarker = new window.TMap.MultiMarker({
          map: this.map,
          styles: {
            cur: new window.TMap.MarkerStyle({
              width: 22, height: 22, src: this._svgPulse('#165DFF'), anchor: { x: 11, y: 11 }
            })
          },
          geometries: [{ styleId: 'cur', position: paths[cur] }]
        })
        // 自适应视野
        this._fitBounds(paths)
      } catch (e) {
        console.error('[TMapPicker track] renderTrack failed:', e)
      }
    },
    updateTrackCurrent() {
      if (!this.map || !this.trackPoints || !this.trackPoints.length) return
      const cur = Math.min(Math.max(this.trackCurrent, 0), this.trackPoints.length - 1)
      const paths = this.trackPoints.map(p => new window.TMap.LatLng(p.lat, p.lng))
      // 重建已走 polyline
      try { this.trackPassedLine && this.trackPassedLine.setMap(null) } catch (e) {}
      this.trackPassedLine = null
      if (cur > 0) {
        try {
          this.trackPassedLine = new window.TMap.MultiPolyline({
            map: this.map,
            styles: { passed: new window.TMap.PolylineStyle({ color: '#165DFF', width: 5, lineCap: 'round' }) },
            geometries: [{ styleId: 'passed', paths: paths.slice(0, cur + 1) }]
          })
        } catch (e) {
          console.error('[TMapPicker track] updateTrackCurrent failed:', e)
        }
      }
      // 更新当前位置 marker
      if (this.trackCurrentMarker) {
        try {
          this.trackCurrentMarker.setGeometries([{ styleId: 'cur', position: paths[cur] }])
        } catch (e) {}
      }
    },
    _splitOverSegments(pts) {
      const segs = []
      let cur = []
      pts.forEach((p, i) => {
        if (p.over) {
          cur.push(p)
        } else {
          if (cur.length > 1) segs.push(cur)
          cur = []
        }
        if (i === pts.length - 1 && cur.length > 1) segs.push(cur)
      })
      return segs
    },
    _fitBounds(paths) {
      try {
        if (!this.map || !window.TMap) return
        const bounds = new window.TMap.LatLngBounds(paths)
        this.map.fitBounds(bounds, { padding: 60 })
      } catch (e) {}
    },
    // 生成 SVG dataURL 标记（彩色水滴标）
    _svgMarker(color, text) {
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='22' height='28' viewBox='0 0 22 28'>
        <path d='M11 0 C5 0 0 5 0 11 C0 19 11 28 11 28 C11 28 22 19 22 11 C22 5 17 0 11 0 Z' fill='${color}' stroke='#fff' stroke-width='1.5'/>
        <text x='11' y='14' text-anchor='middle' fill='#fff' font-size='10' font-weight='700' font-family='Arial'>${text}</text>
      </svg>`
      return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)
    },
    // 当前位置脉冲圆 SVG
    _svgPulse(color) {
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='22' height='22' viewBox='0 0 22 22'>
        <circle cx='11' cy='11' r='10' fill='${color}' fill-opacity='0.25'/>
        <circle cx='11' cy='11' r='6' fill='${color}' fill-opacity='0.5' stroke='#fff' stroke-width='1.5'/>
        <circle cx='11' cy='11' r='2.5' fill='#fff'/>
      </svg>`
      return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg)
    },
    onPointClick(e) {
      const ll = e.latLng
      const val = { lat: +ll.lat.toFixed(6), lng: +ll.lng.toFixed(6) }
      this.placePoint(val)
      this.$emit('change', val)
    },
    placePoint(val) {
      if (!this.markerLayer) {
        this.markerLayer = new window.TMap.MultiMarker({
          map: this.map,
          geometries: []
        })
      }
      this.markerLayer.setGeometries([
        { id: 'p', position: new window.TMap.LatLng(val.lat, val.lng) }
      ])
      this.map.setCenter(new window.TMap.LatLng(val.lat, val.lng))
    },
    toggleDraw() {
      this.drawing = !this.drawing
    },
    onDblClick() {
      if (this.drawing) this.drawing = false
    },
    onZoneClick(e) {
      if (!this.drawing) return
      const ll = e.latLng
      this.points.push({ lat: +ll.lat.toFixed(6), lng: +ll.lng.toFixed(6) })
      this.redrawAll()
      this.$emit('change', this.points.slice())
    },
    onPointMarkerClick(e) {
      const id = e && e.geometry ? e.geometry.id : ''
      const m = /^pt-(\d+)$/.exec(id)
      if (!m) return
      const idx = +m[1]
      if (idx >= 0 && idx < this.points.length) {
        this.points.splice(idx, 1)
        this.redrawAll()
        this.$emit('change', this.points.slice())
      }
    },
    redrawAll() {
      this.drawPolygon()
      this.drawPolyline()
      this.drawPointMarkers()
    },
    drawPolygon() {
      if (this.points.length < 3) {
        if (this.polygon) { this.polygon.setMap(null); this.polygon = null }
        return
      }
      const paths = this.points.map(p => new window.TMap.LatLng(p.lat, p.lng))
      if (!this.polygon) {
        this.polygon = new window.TMap.MultiPolygon({
          map: this.map,
          styles: {
            'default': new window.TMap.PolygonStyle({
              color: 'rgba(22, 93, 255, 0.25)',
              showBorder: true,
              borderColor: '#165DFF',
              borderWidth: 2
            })
          },
          geometries: [{ styleId: 'default', paths: [paths] }]
        })
      } else {
        this.polygon.setGeometries([{ styleId: 'default', paths: [paths] }])
      }
    },
    drawPolyline() {
      if (this.points.length < 2) {
        if (this.polyline) { this.polyline.setMap(null); this.polyline = null }
        return
      }
      const paths = this.points.map(p => new window.TMap.LatLng(p.lat, p.lng))
      if (!this.polyline) {
        this.polyline = new window.TMap.MultiPolyline({
          map: this.map,
          styles: {
            'dash': new window.TMap.PolylineStyle({
              color: '#165DFF',
              width: 3,
              lineDash: [8, 6]
            })
          },
          geometries: [{ styleId: 'dash', paths: [paths] }]
        })
      } else {
        this.polyline.setGeometries([{ styleId: 'dash', paths: [paths] }])
      }
    },
    drawPointMarkers() {
      if (!this.pointLayer) {
        this.pointLayer = new window.TMap.MultiMarker({
          map: this.map,
          styles: {
            'point': new window.TMap.MarkerStyle({
              width: 22,
              height: 22,
              anchor: { x: 11, y: 11 },
              color: '#165DFF'
            })
          },
          geometries: []
        })
        if (!this.pointClickBound) {
          this.pointLayer.on('click', this.onPointMarkerClick)
          this.pointClickBound = true
        }
      }
      const geometries = this.points.map((p, i) => ({
        id: 'pt-' + i,
        styleId: 'point',
        position: new window.TMap.LatLng(p.lat, p.lng),
        properties: { index: i }
      }))
      this.pointLayer.setGeometries(geometries)
      this.drawPointLabels()
    },
    drawPointLabels() {
      if (!window.TMap.MultiLabel) return
      try {
        if (!this.labelLayer) {
          this.labelLayer = new window.TMap.MultiLabel({
            map: this.map,
            styles: {
              'label': new window.TMap.LabelStyle({
                color: '#fff',
                size: 12,
                offset: { x: 0, y: -26 },
                anchor: 'center',
                backgroundColor: '#165DFF',
                padding: '2px 6px',
                borderRadius: 10
              })
            },
            geometries: []
          })
        }
        const geometries = this.points.map((p, i) => ({
          id: 'lb-' + i,
          styleId: 'label',
          position: new window.TMap.LatLng(p.lat, p.lng),
          content: String(i + 1)
        }))
        this.labelLayer.setGeometries(geometries)
      } catch (e) {
        console.warn('TMap label init failed', e)
      }
    },
    undoPoint() {
      this.points.pop()
      this.redrawAll()
      this.$emit('change', this.points.slice())
    },
    clearPoints() {
      this.points = []
      this.redrawAll()
      this.$emit('change', [])
    },
    renderMarkers() {
      if (!this.map) return
      if (!this.markerLayer) {
        this.markerLayer = new window.TMap.MultiMarker({ map: this.map, geometries: [] })
      }
      const list = (this.markers || []).map((m, i) => ({
        id: String(m.id || i),
        position: new window.TMap.LatLng(+m.lat, +m.lng),
        properties: { item: m }
      }))
      this.markerLayer.setGeometries(list)
      this.markerLayer.on('click', (e) => {
        const item = e.geometry && e.geometry.properties ? e.geometry.properties.item : null
        if (item) this.$emit('marker-click', item)
      })
    }
  }
}
</script>

<style scoped>
.tmap-picker {
  position: relative;
  width: 100%;
}
.tmap-el {
  width: 100%;
  background: #eef0f3;
  border-radius: var(--border-radius);
}
.tmap-el.drawing {
  cursor: crosshair;
}
.tmap-tip {
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 4px;
}
.tmap-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  flex-wrap: wrap;
}
.tmap-hint {
  font-size: 12px;
  color: var(--text-200);
}
</style>
